/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import DisclaimerFooter from "components/DisclaimerFooter"
import { useDataInfo } from "hooks/useDataInfo"
import { useEffect } from "react"
import { Link } from "react-router"
import { countSimulationCohorts } from "utils/dateUtils"
import { pageMetaData, updateMetaTags } from "utils/metaTags"

const About = () => {
  const { dataInfo, loading, error } = useDataInfo()
  const cohortCount =
    loading || error || !dataInfo?.maxSimulationStartYear
      ? null
      : countSimulationCohorts(dataInfo.earliestDataDate, dataInfo.maxSimulationStartYear)

  // Update meta tags for about page
  useEffect(() => {
    const { title, description, keywords } = pageMetaData.about
    updateMetaTags(title, description, keywords)
  }, [])

  return (
    <div className="pt-1 pb-3">
      <h2 className="fs-4 mb-3">Overview</h2>
      <p>
        Safe Retirement Spending is a retirement calculator that uses historical market data to
        simulate how your plan would have performed over various periods of the past, and under
        various market conditions. We simulate up to{" "}
        {cohortCount === null ? "loading" : cohortCount.toLocaleString()} retirements, ranging in
        their beginning months from{" "}
        {loading ? "loading" : error ? "loading" : dataInfo?.earliestDataDate} to{" "}
        {loading ? "loading" : error ? "loading" : `12/${dataInfo?.maxSimulationStartYear}`}.
      </p>
      <h2 className="fs-4 mb-3">What Makes it Different?</h2>
      <ul className="mb-3">
        <li>
          Rather than using average returns or Monte Carlo simulations, we use actual historical
          sequences of returns.
        </li>
        <li>
          You can filter the retirements simulated by multiple salient factors: CAPE ratios and
          stock market drawdowns at the simulations&apos; beginnings, and by starting years.
        </li>
        <li>We use monthly, as opposed to annual, return sequences.</li>
        <li>You can define &apos;glide paths&apos; between asset allocations.</li>
      </ul>
      <p>
        See our <Link to="/methodology/">methodology page</Link> for more details on these choices
        and their implementations.
      </p>
      <h2 className="fs-4 mb-3">Who We Are</h2>
      {/* AIDEV-NOTE: #935 - the attribution and both sets of credentials are deliberate and
          approved (#522, 2026-08-15); do not remove them. What was removed is the superlatives
          ("most respected", "widely-acclaimed", "gold standard"): a credential states who
          someone is, a superlative invites reliance, and reliance is what the
          negligent-misrepresentation defence needs to be unreasonable.
          A paragraph scoping the collaboration used to follow this one, and was dropped as
          redundant - "Why We Built This" immediately below already tells the reader the
          methodology is Karsten's, so the scoping sentence only previewed it. Do not
          reinstate one without a reason that survives that objection.
          Guarded by utils/legalCopy.test.ts. */}
      <p>
        Safe Retirement Spending was created by John Firth, PhD, in collaboration with Karsten
        &quot;Big ERN&quot; Jeske, PhD, CFA. Karsten is the author of the{" "}
        <a href="https://earlyretirementnow.com/" target="_blank" rel="noopener noreferrer">
          Early Retirement Now blog
        </a>{" "}
        and its Safe Withdrawal Rate series, which is widely read within the FIRE (Financial
        Independence, Retire Early) community. John is a software engineer and data scientist with
        17 years of professional experience.
      </p>
      <h2 className="fs-4 mt-4 mb-3">Why We Built This</h2>
      <p>
        Safe Retirement Spending is based on the same methodology used in Karsten&apos;s&nbsp;
        <a
          href="https://earlyretirementnow.com/2018/08/29/google-sheet-updates-swr-series-part-28/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Sheet-based SWR Toolbox
        </a>
        . The toolbox is widely used in the FIRE community, but some find its spreadsheet format
        somewhat inaccessible. We hope encapsulating it within a dedicated app will allow more to
        benefit from the methodology. Also, some features &mdash; like glide paths for multiple
        starting months, user-defined data filters, and some charts &mdash; are difficult to create
        with Google Sheets, but are part of this app. Conversely, the Google sheet has some
        functionality that the app doesn&apos;t currently support, such as scaling of withdrawal
        rates over time, and custom returns sequences. The Google sheet isn&apos;t going away, and
        we encourage you to use it if you prefer that format or need those features!
      </p>
      <h2 className="fs-4 mt-4 mb-3">Which Data Do We Use?</h2>
      <p>
        Our dataset currently covers{" "}
        {loading ? "2/1871" : error ? "2/1871" : dataInfo?.earliestDataDate} -{" "}
        {loading ? "present" : error ? "present" : dataInfo?.latestDataDate}, using the same
        authoritative sources as <em>Early Retirement Now</em> like Robert Shiller&apos;s historical
        market data, Federal Reserve Economic Data (FRED), and Fama-French research files. See our{" "}
        {/* AIDEV-NOTE: plain <a>, not <Link> — Link performs client-side nav and doesn't
            scroll to hash fragments; this app has no ScrollRestoration/hash-scroll handling */}
        <a href="/methodology/#data-sources">methodology page</a> for a more thorough breakdown of
        data sources and how results are checked.
      </p>
      <h2 className="fs-4 mt-4 mb-3">Contact Us</h2>
      <p>
        If you have questions or suggestions, please get in touch with us at{" "}
        <a href="mailto:support&#64;saferetirementspending&#46;com">
          support&#64;saferetirementspending&#46;com
        </a>
        .
      </p>

      <DisclaimerFooter />
    </div>
  )
}

export default About
